import {
  buildAmortisationSchedule,
  format,
  MonthlyRepayment,
  PAYMENTS_PER_YEAR,
  pmt,
  RepaymentFrequency,
  YearlyRepayment,
} from '@/lib/calculators/pmtUtils';

export { PAYMENTS_PER_YEAR };
export type { RepaymentFrequency };

type LoanInput = {
  loanAmount: number;
  interestRate: number; // annual rate in %
  loanTermMonths: number; // in months
  balloonPaymentPercent?: number; // for Equipment finance
};

type RepaymentResult = {
  monthly: MonthlyRepayment[];
  yearly: YearlyRepayment[];
  loanAmount: number;
  totalPayment: string;
  totalInterest: string;
  monthlyPayment: string;
  apr?: number;
  factorRate?: number;
};

// ─── New result types ─────────────────────────────────────────────────────────

export type LoanRepaymentResult = {
  periodicPayment: string;
  paymentLabel: string;
  totalInterest: string;
  totalRepaid: string;
  loanAmount: number;
  schedule: MonthlyRepayment[];
  yearly: YearlyRepayment[];
};

export type CarLoanResult = {
  netLoan: number;
  periodicPayment: string;
  totalInterest: string;
  totalRepaid: string;
  avgAnnualInterest: string;
  schedule: MonthlyRepayment[];
  yearly: YearlyRepayment[];
};

export type ChattelMortgageResult = CarLoanResult & {
  gstClaimable: string;
  netCostAfterGST: string;
};

export type HirePurchaseResult = CarLoanResult & {
  totalAcquisitionCost: string;
};

export type CommercialPropertyLoanResult = {
  monthlyPayment: string;
  totalInterest: string;
  totalRepaid: string;
  lvr: number;
  lvrWarning: boolean;
  totalCostIncludingDuty: string;
  schedule: MonthlyRepayment[];
  yearly: YearlyRepayment[];
};

// ─── New calculation functions ────────────────────────────────────────────────

function calculateLoanRepayment({
  loanAmount,
  interestRate,
  loanTermYears,
  balloonPaymentPercent = 0,
  repaymentFrequency = 'monthly',
}: {
  loanAmount: number;
  interestRate: number;
  loanTermYears: number;
  balloonPaymentPercent?: number;
  repaymentFrequency?: RepaymentFrequency;
}): LoanRepaymentResult {
  const paymentsPerYear = PAYMENTS_PER_YEAR[repaymentFrequency];
  const periodicRate = interestRate / 100 / paymentsPerYear;
  const nPeriods = loanTermYears * paymentsPerYear;
  const balloonAmount = (balloonPaymentPercent / 100) * loanAmount;
  const paymentLabels: Record<RepaymentFrequency, string> = {
    monthly: 'Monthly',
    fortnightly: 'Fortnightly',
    weekly: 'Weekly',
  };

  if (loanAmount <= 0 || loanTermYears <= 0) {
    return {
      periodicPayment: '0.00',
      paymentLabel: paymentLabels[repaymentFrequency],
      totalInterest: '0.00',
      totalRepaid: '0.00',
      loanAmount,
      schedule: [],
      yearly: [],
    };
  }

  const periodicPayment = pmt(periodicRate, nPeriods, -loanAmount, balloonAmount);
  const totalRepaid = periodicPayment * nPeriods + balloonAmount;
  const totalInterest = Math.max(totalRepaid - loanAmount, 0);

  const { schedule, yearly } = buildAmortisationSchedule({
    loanAmount,
    periodicRate,
    nPeriods,
    periodicPayment,
    balloonAmount,
    paymentsPerYear,
  });

  return {
    periodicPayment: format(periodicPayment),
    paymentLabel: paymentLabels[repaymentFrequency],
    totalInterest: format(totalInterest),
    totalRepaid: format(totalRepaid),
    loanAmount,
    schedule,
    yearly,
  };
}

function calculateCarLoan({
  vehiclePrice,
  deposit,
  interestRate,
  loanTermYears,
  balloonPaymentPercent = 0,
}: {
  vehiclePrice: number;
  deposit: number;
  interestRate: number;
  loanTermYears: number;
  balloonPaymentPercent?: number;
}): CarLoanResult {
  const netLoan = Math.max(vehiclePrice - deposit, 0);
  const balloonAmount = (balloonPaymentPercent / 100) * netLoan;
  const periodicRate = interestRate / 100 / 12;
  const nPeriods = loanTermYears * 12;

  if (netLoan <= 0 || loanTermYears <= 0) {
    return {
      netLoan,
      periodicPayment: '0.00',
      totalInterest: '0.00',
      totalRepaid: '0.00',
      avgAnnualInterest: '0.00',
      schedule: [],
      yearly: [],
    };
  }

  const periodicPayment = pmt(periodicRate, nPeriods, -netLoan, balloonAmount);
  const totalRepaid = periodicPayment * nPeriods + balloonAmount;
  const totalInterest = Math.max(totalRepaid - netLoan, 0);
  const avgAnnualInterest = totalInterest / loanTermYears;

  const { schedule, yearly } = buildAmortisationSchedule({
    loanAmount: netLoan,
    periodicRate,
    nPeriods,
    periodicPayment,
    balloonAmount,
    paymentsPerYear: 12,
  });

  return {
    netLoan,
    periodicPayment: format(periodicPayment),
    totalInterest: format(totalInterest),
    totalRepaid: format(totalRepaid),
    avgAnnualInterest: format(avgAnnualInterest),
    schedule,
    yearly,
  };
}

function calculateChattelMortgage({
  vehiclePrice,
  deposit,
  interestRate,
  loanTermYears,
  balloonPaymentPercent = 0,
  gstClaimablePercent = 100,
}: {
  vehiclePrice: number;
  deposit: number;
  interestRate: number;
  loanTermYears: number;
  balloonPaymentPercent?: number;
  gstClaimablePercent?: number;
}): ChattelMortgageResult {
  const netLoan = Math.max(vehiclePrice - deposit, 0);
  const balloonAmount = (balloonPaymentPercent / 100) * netLoan;
  const periodicRate = interestRate / 100 / 12;
  const nPeriods = loanTermYears * 12;
  // GST claimable from GST-inclusive vehicle price (GST = price / 11)
  const gstClaimable = (vehiclePrice / 11) * (gstClaimablePercent / 100);

  if (netLoan <= 0 || loanTermYears <= 0) {
    return {
      netLoan,
      periodicPayment: '0.00',
      totalInterest: '0.00',
      totalRepaid: '0.00',
      avgAnnualInterest: '0.00',
      gstClaimable: format(gstClaimable),
      netCostAfterGST: '0.00',
      schedule: [],
      yearly: [],
    };
  }

  const periodicPayment = pmt(periodicRate, nPeriods, -netLoan, balloonAmount);
  const totalRepaid = periodicPayment * nPeriods + balloonAmount;
  const totalInterest = Math.max(totalRepaid - netLoan, 0);
  const avgAnnualInterest = totalInterest / loanTermYears;
  const netCostAfterGST = Math.max(totalRepaid - gstClaimable, 0);

  const { schedule, yearly } = buildAmortisationSchedule({
    loanAmount: netLoan,
    periodicRate,
    nPeriods,
    periodicPayment,
    balloonAmount,
    paymentsPerYear: 12,
  });

  return {
    netLoan,
    periodicPayment: format(periodicPayment),
    totalInterest: format(totalInterest),
    totalRepaid: format(totalRepaid),
    avgAnnualInterest: format(avgAnnualInterest),
    gstClaimable: format(gstClaimable),
    netCostAfterGST: format(netCostAfterGST),
    schedule,
    yearly,
  };
}

function calculateHirePurchase({
  vehiclePrice,
  deposit,
  interestRate,
  loanTermYears,
  balloonPaymentPercent = 0,
}: {
  vehiclePrice: number;
  deposit: number;
  interestRate: number;
  loanTermYears: number;
  balloonPaymentPercent?: number;
}): HirePurchaseResult {
  const netLoan = Math.max(vehiclePrice - deposit, 0);
  const balloonAmount = (balloonPaymentPercent / 100) * netLoan;
  const periodicRate = interestRate / 100 / 12;
  const nPeriods = loanTermYears * 12;

  if (netLoan <= 0 || loanTermYears <= 0) {
    return {
      netLoan,
      periodicPayment: '0.00',
      totalInterest: '0.00',
      totalRepaid: '0.00',
      avgAnnualInterest: '0.00',
      totalAcquisitionCost: format(deposit),
      schedule: [],
      yearly: [],
    };
  }

  const periodicPayment = pmt(periodicRate, nPeriods, -netLoan, balloonAmount);
  const totalRepaid = periodicPayment * nPeriods + balloonAmount;
  const totalInterest = Math.max(totalRepaid - netLoan, 0);
  const avgAnnualInterest = totalInterest / loanTermYears;
  const totalAcquisitionCost = deposit + totalRepaid;

  const { schedule, yearly } = buildAmortisationSchedule({
    loanAmount: netLoan,
    periodicRate,
    nPeriods,
    periodicPayment,
    balloonAmount,
    paymentsPerYear: 12,
  });

  return {
    netLoan,
    periodicPayment: format(periodicPayment),
    totalInterest: format(totalInterest),
    totalRepaid: format(totalRepaid),
    avgAnnualInterest: format(avgAnnualInterest),
    totalAcquisitionCost: format(totalAcquisitionCost),
    schedule,
    yearly,
  };
}

function calculateCommercialPropertyLoan({
  loanAmount,
  propertyValue,
  interestRate,
  loanTermYears,
  stampDutyAndCosts,
}: {
  loanAmount: number;
  propertyValue: number;
  interestRate: number;
  loanTermYears: number;
  stampDutyAndCosts: number;
}): CommercialPropertyLoanResult {
  const periodicRate = interestRate / 100 / 12;
  const nPeriods = loanTermYears * 12;
  const lvr = propertyValue > 0 ? (loanAmount / propertyValue) * 100 : 0;
  const lvrWarning = lvr > 70;

  if (loanAmount <= 0 || loanTermYears <= 0) {
    return {
      monthlyPayment: '0.00',
      totalInterest: '0.00',
      totalRepaid: '0.00',
      lvr,
      lvrWarning,
      totalCostIncludingDuty: format(stampDutyAndCosts),
      schedule: [],
      yearly: [],
    };
  }

  const monthlyPayment = pmt(periodicRate, nPeriods, -loanAmount, 0);
  const totalRepaid = monthlyPayment * nPeriods;
  const totalInterest = Math.max(totalRepaid - loanAmount, 0);
  const totalCostIncludingDuty = totalRepaid + stampDutyAndCosts;

  const { schedule, yearly } = buildAmortisationSchedule({
    loanAmount,
    periodicRate,
    nPeriods,
    periodicPayment: monthlyPayment,
    balloonAmount: 0,
    paymentsPerYear: 12,
  });

  return {
    monthlyPayment: format(monthlyPayment),
    totalInterest: format(totalInterest),
    totalRepaid: format(totalRepaid),
    lvr,
    lvrWarning,
    totalCostIncludingDuty: format(totalCostIncludingDuty),
    schedule,
    yearly,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useLoanRepaymentCalculator() {
  // format is now module-level; kept as inner alias for the legacy calculateLoan closure below
  const fmt = format;

  function calculateLoan({
    loanAmount,
    interestRate,
    loanTermMonths,
    balloonPaymentPercent = 0,
  }: LoanInput): RepaymentResult {
    const balloonAmount = (balloonPaymentPercent / 100) * loanAmount;
    const principal = loanAmount - balloonAmount;
    const monthlyRate = interestRate / 1200;
    const n = loanTermMonths;

    if (principal <= 0 || interestRate <= 0 || n <= 0) {
      return {
        loanAmount,
        monthly: [],
        yearly: [],
        totalPayment: '0.00',
        totalInterest: '0.00',
        monthlyPayment: '0.00',
      };
    }

    const pow = Math.pow(1 + monthlyRate, n);
    const monthlyPayment =
      ((principal + balloonAmount) * monthlyRate * pow) / (pow - 1) -
      (monthlyRate / (pow - 1)) * balloonAmount;

    let balance = principal + balloonAmount;
    let totalInterest = 0;
    const monthly: MonthlyRepayment[] = [];
    const yearly: YearlyRepayment[] = [];

    let thisYearInterest = 0;
    let _thisYearPrincipal = 0;

    for (let i = 1; i <= n; i++) {
      const beginningBalance = balance;
      const interest = +(beginningBalance * monthlyRate).toFixed(2);
      const principalPaid = +(monthlyPayment - interest).toFixed(2);
      balance = +(beginningBalance - principalPaid).toFixed(2);
      totalInterest += interest;
      thisYearInterest += interest;
      _thisYearPrincipal += principalPaid;

      monthly.push({
        Number: i,
        Payment: fmt(monthlyPayment),
        Interest: fmt(interest),
        Principal: fmt(principalPaid),
        Balance: fmt(Math.max(balance, 0)),
      });

      if (i % 12 === 0 || i === n) {
        const monthsInYear = i % 12 || 12;
        yearly.push({
          Year: i === n && balloonAmount > 0 ? 'Final' : i / 12,
          totalpayments: fmt(monthlyPayment * monthsInYear),
          totalinterest: fmt(thisYearInterest),
          totalprincipal: fmt(monthlyPayment * monthsInYear - thisYearInterest),
          totalbalance: fmt(Math.max(balance, 0)),
        });
        thisYearInterest = 0;
        _thisYearPrincipal = 0;
      }
    }

    if (balloonAmount > 0) {
      monthly.push({
        Number: 'Balloon',
        Payment: fmt(balloonAmount),
        Interest: fmt(0),
        Principal: fmt(balloonAmount),
        Balance: fmt(0),
      });

      yearly.push({
        Year: 'Balloon',
        totalpayments: fmt(balloonAmount),
        totalinterest: fmt(0),
        totalprincipal: fmt(balloonAmount),
        totalbalance: fmt(0),
      });
    }

    const totalPayment = fmt(monthlyPayment * n + balloonAmount);
    return {
      loanAmount,
      monthly,
      yearly,
      totalPayment,
      totalInterest: fmt(totalInterest),
      monthlyPayment: fmt(monthlyPayment),
    };
  }

  const calculateBusinessLoanRepayments = (input: LoanInput) =>
    calculateLoan({ ...input, balloonPaymentPercent: 0 });

  const calculateBusinessLineOfCreditRepayments = (input: LoanInput) =>
    calculateLoan({ ...input, balloonPaymentPercent: 0 });

  const calculateEquipmentFinanceRepayments = (input: LoanInput) => calculateLoan(input);

  return {
    calculateBusinessLoanRepayments,
    calculateBusinessLineOfCreditRepayments,
    calculateEquipmentFinanceRepayments,
    calculateLoanRepayment,
    calculateCarLoan,
    calculateChattelMortgage,
    calculateHirePurchase,
    calculateCommercialPropertyLoan,
  };
}
